"use strict";
/**
 * Tenant billing lifecycle — ONE pure reducer for every subscription transition.
 *
 * Signup, trial, payment failure, grace, reminders, suspension, recovery,
 * admin suspend/unsuspend/comp and cancellation all resolve here, so the
 * webhook (event-driven) and the daily cron (time-driven) can never disagree.
 *
 * Pure by design: no AWS, no Stripe, no clock. Callers pass `now` and perform
 * the writes described by the returned actions.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GRACE_DAYS = void 0;
exports.initialBillingState = initialBillingState;
exports.accessOf = accessOf;
exports.applyBillingEvent = applyBillingEvent;
const tenantAccess_1 = require("./tenantAccess");
exports.GRACE_DAYS = 7;
const DAY_MS = 86400000;
/** Access flags derived from status. Reports and billing are NEVER withdrawn. */
function allowFor(status) {
    const operating = status !== 'suspended';
    return {
        pos_sell: operating,
        online_order: operating,
        kiosk: operating,
        admin_app: operating,
        reports: true, // a suspended tenant must still see their own data
        billing_page: true, // ...and must always be able to pay
    };
}
function settle(s) {
    return { ...s, allow: allowFor(s.status) };
}
function initialBillingState() {
    return settle({ status: 'active', subscriptionStatus: 'incomplete', graceUntil: null, suspendReason: null });
}
/** Derive the shared access primitive from lifecycle state. */
function accessOf(state, now) {
    return (0, tenantAccess_1.tenantAccessState)(state, now);
}
function applyBillingEvent(state, event, now) {
    const actions = [];
    const before = state.status;
    const comped = state.subscriptionStatus === 'comped';
    switch (event.type) {
        case 'checkout_completed': {
            // A new/renewed subscription clears payment and cancellation lockouts,
            // but must NOT quietly undo a deliberate admin suspension.
            if (state.suspendReason === 'admin')
                return { next: state, actions };
            const trialing = (event.trialDays ?? 0) > 0;
            actions.push({ type: 'email', template: 'payment-setup', to: 'tenant' });
            return {
                next: settle({
                    status: 'active',
                    subscriptionStatus: trialing ? 'trialing' : 'active',
                    graceUntil: null,
                    suspendReason: null,
                }),
                actions,
            };
        }
        case 'invoice_paid': {
            if (state.suspendReason === 'admin')
                return { next: state, actions }; // admin lock survives payment
            if (state.status !== 'active' || state.subscriptionStatus !== 'active') {
                if (before !== 'active') {
                    actions.push({ type: 'notify_super_admin', event: 'tenant_payment_recovered' });
                }
            }
            return {
                next: settle({ status: 'active', subscriptionStatus: 'active', graceUntil: null, suspendReason: null }),
                actions,
            };
        }
        case 'invoice_payment_failed': {
            if (comped)
                return { next: state, actions }; // comped tenants owe nothing
            if (state.suspendReason === 'admin')
                return { next: state, actions };
            // Already in grace or suspended for payment: do NOT restart the clock
            // and do NOT re-email. The window is anchored to the FIRST failure.
            if (state.status === 'grace' || state.suspendReason === 'payment')
                return { next: state, actions };
            actions.push({ type: 'email', template: 'payment-failed', to: 'tenant', data: { daysToSuspension: exports.GRACE_DAYS } });
            actions.push({ type: 'notify_super_admin', event: 'tenant_payment_failed' });
            return {
                next: settle({
                    status: 'grace',
                    subscriptionStatus: 'past_due',
                    graceUntil: now + exports.GRACE_DAYS * DAY_MS,
                    suspendReason: null,
                }),
                actions,
            };
        }
        case 'day_tick': {
            if (state.status !== 'grace')
                return { next: state, actions }; // healthy or already suspended: nothing to do
            const deadline = state.graceUntil;
            if (typeof deadline !== 'number') { // repair bad data rather than suspend on it
                return {
                    next: settle({ ...state, graceUntil: now + exports.GRACE_DAYS * DAY_MS }),
                    actions,
                };
            }
            if (now >= deadline) {
                actions.push({ type: 'email', template: 'account-suspended', to: 'tenant' });
                actions.push({ type: 'email', template: 'admin-tenant-suspended', to: 'super_admin' });
                actions.push({ type: 'notify_super_admin', event: 'tenant_suspended_unpaid' });
                return {
                    next: settle({ status: 'suspended', subscriptionStatus: 'unpaid', graceUntil: null, suspendReason: 'payment' }),
                    actions,
                };
            }
            const daysLeft = Math.ceil((deadline - now) / DAY_MS);
            actions.push({ type: 'email', template: 'payment-failed', to: 'tenant', data: { daysToSuspension: daysLeft } });
            return { next: state, actions };
        }
        case 'subscription_cancelled': {
            actions.push({ type: 'notify_super_admin', event: 'tenant_cancelled' });
            return {
                next: settle({ status: 'suspended', subscriptionStatus: 'cancelled', graceUntil: null, suspendReason: 'cancelled' }),
                actions,
            };
        }
        case 'admin_suspend': {
            actions.push({ type: 'audit', action: 'admin_suspend', reason: event.reason, before, after: 'suspended' });
            return {
                next: settle({ ...state, status: 'suspended', graceUntil: null, suspendReason: 'admin' }),
                actions,
            };
        }
        case 'admin_unsuspend': {
            actions.push({ type: 'audit', action: 'admin_unsuspend', reason: event.reason, before, after: 'active' });
            return {
                next: settle({ ...state, status: 'active', graceUntil: null, suspendReason: null }),
                actions,
            };
        }
        case 'admin_comp': {
            actions.push({ type: 'audit', action: 'admin_comp', reason: event.reason, before, after: 'active' });
            return {
                next: settle({ status: 'active', subscriptionStatus: 'comped', graceUntil: null, suspendReason: null }),
                actions,
            };
        }
    }
}
//# sourceMappingURL=billingLifecycle.js.map